import amaLog from '../helpers/log';
import { isMobile, isTablet } from '../config/device';

/**
 * ADR: Perf Event Collection
 * 
 * Tracks 6 startup performance metrics emitted once when first ad slot requests.
 * Design decisions:
 * - Device classification: UA-based (tablet→mobile→desktop) not viewport-based.
 *   Rationale: Telemetry should reflect actual hardware, not rendering breakpoints.
 *   Tablet priority: Some tablets report as mobile in UA; regex patterns match tablet first.
 * - Lazy device classification: Deferred until emit to handle test environments without navigator.
 * - Single emission: Deferred until first slot request ensures ad setup is complete.
 * - Gating: Only emits if no active consent interaction + within samplerate.
 */
type PerfEventData = {
  bundleEvalMs: number | null;
  accConfigMs: number | null;
  firstSlotRequestedMs: number | null;
  consentMissingInBid: number;
  device: string | null;
  userAgent: string;
};

class PerfEventCollector {
  private data: PerfEventData = {
    bundleEvalMs: null,
    accConfigMs: null,
    firstSlotRequestedMs: null,
    consentMissingInBid: 0,
    device: null,
    userAgent: '',
  };

  private bundleStartMs: number | null = null;
  private accConfigStartMs: number | null = null;
  private firstSlotMarked = false;
  private emitWhenFirstSlotRequested = false;
  private perfStatEmitted = false;
  private deviceClassified = false;

  private getNow(): number | null {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return null;
  }

  private ensureDeviceClassified(): void {
    if (!this.deviceClassified) {
      this.deviceClassified = true;
      // Classify device based on UA patterns: tablet > mobile > desktop
      // Priority: tablet check first (some tablets report as mobile), then mobile, else desktop
      if (typeof navigator !== 'undefined' && navigator.userAgent) {
        this.data.userAgent = navigator.userAgent;
        if (isTablet) {
          this.data.device = 'tablet';
        } else if (isMobile) {
          this.data.device = 'mobile';
        } else {
          this.data.device = 'desktop';
        }
        amaLog(`PerfEvent: device=${this.data.device}`);
      }
    }
  }

  public markBundleEvalStart(): void {
    const now = this.getNow();
    if (now !== null) {
      this.bundleStartMs = now;
    }
  }

  public markBundleEvalEnd(): void {
    if (this.bundleStartMs !== null) {
      this.data.bundleEvalMs = Math.round(this.bundleStartMs);
      amaLog(`PerfEvent: bundle_eval_ms = ${this.data.bundleEvalMs}`);
    }
  }

  public markAccConfigStart(): void {
    const now = this.getNow();
    if (now !== null) {
      this.accConfigStartMs = now;
    }
  }

  public markAccConfigEnd(): void {
    if (this.accConfigStartMs !== null) {
      const now = this.getNow();
      if (now !== null) {
        this.data.accConfigMs = Math.round(now - this.accConfigStartMs);
        amaLog(`PerfEvent: acc_config_ms = ${this.data.accConfigMs}`);
      }
    }
  }

  public incrementConsentMissingInBid(): void {
    this.data.consentMissingInBid += 1;
    amaLog(`PerfEvent: consent_missing_in_bid = ${this.data.consentMissingInBid}`);
  }

  public setEmitWhenFirstSlotRequested(shouldEmit: boolean): void {
    this.emitWhenFirstSlotRequested = shouldEmit;
  }

  public markFirstSlotRequested(): boolean {
    // Only record the first slot request once.
    if (this.firstSlotMarked) {
      return false;
    }
    if (this.bundleStartMs === null) {
      return false;
    }

    const now = this.getNow();
    if (now !== null) {
      this.data.firstSlotRequestedMs = Math.round(now);
      this.firstSlotMarked = true;
      amaLog(`PerfEvent: first_slot_requested_ms = ${this.data.firstSlotRequestedMs}`);
      return true;
    }

    return false;
  }

  public emitIfReady(): void {
    if (!this.emitWhenFirstSlotRequested || !this.firstSlotMarked || this.perfStatEmitted) {
      return;
    }

    this.ensureDeviceClassified();

    if (window.ama?.adStats) {
      window.ama.adStats.add([this.toStats()]);
      window.ama.adStats.push();
      this.perfStatEmitted = true;
      amaLog('PerfEvent: emitted via AdStats after first slot request');
    }
  }

  public toStats() {

    return {
      bundle_eval_ms: this.data.bundleEvalMs,
      acc_config_ms: this.data.accConfigMs,
      first_slot_requested_ms: this.data.firstSlotRequestedMs,
      consent_missing_in_bid: this.data.consentMissingInBid,
      device: this.data.device,
      user_agent: this.data.userAgent,
    };
  }
}

export const perfEventCollector = new PerfEventCollector();
